/**
 * GUS Admin Portal Design System - Responsive System
 * Unified breakpoints and responsive utilities
 */

import { useState, useEffect } from 'react';
import { css } from 'styled-components';

// =============================================================================
// UNIFIED BREAKPOINTS
// =============================================================================

export const breakpoints = {
  xs: 480,    // Small phones (320-480px)
  sm: 768,    // Large phones, small tablets (481-768px)
  md: 1024,   // Surface Pro, iPad vertical, tablets (769-1024px)
  lg: 1280,   // MacBook Air 13", small laptops (1025-1280px)
  xl: 1440,   // MacBook Pro 15", standard desktops (1281-1440px)
  '2xl': 1680, // Large desktops (1441+)
} as const;

export type BreakpointKey = keyof typeof breakpoints;

// =============================================================================
// MEDIA QUERY HELPERS
// =============================================================================

export const media = {
  // Max-width queries (most common - "below this breakpoint")
  belowXs: `@media (max-width: ${breakpoints.xs - 1}px)`,
  belowSm: `@media (max-width: ${breakpoints.sm - 1}px)`,
  belowMd: `@media (max-width: ${breakpoints.md - 1}px)`,
  belowLg: `@media (max-width: ${breakpoints.lg - 1}px)`,
  belowXl: `@media (max-width: ${breakpoints.xl - 1}px)`,
  below2xl: `@media (max-width: ${breakpoints['2xl'] - 1}px)`,

  // Min-width queries ("above this breakpoint")
  aboveXs: `@media (min-width: ${breakpoints.xs}px)`,
  aboveSm: `@media (min-width: ${breakpoints.sm}px)`,
  aboveMd: `@media (min-width: ${breakpoints.md}px)`,
  aboveLg: `@media (min-width: ${breakpoints.lg}px)`,
  aboveXl: `@media (min-width: ${breakpoints.xl}px)`,
  above2xl: `@media (min-width: ${breakpoints['2xl']}px)`,

  // Range queries
  xsOnly: `@media (max-width: ${breakpoints.xs - 1}px)`,
  smOnly: `@media (min-width: ${breakpoints.xs}px) and (max-width: ${breakpoints.sm - 1}px)`,
  mdOnly: `@media (min-width: ${breakpoints.sm}px) and (max-width: ${breakpoints.md - 1}px)`,
  lgOnly: `@media (min-width: ${breakpoints.md}px) and (max-width: ${breakpoints.lg - 1}px)`,
  xlOnly: `@media (min-width: ${breakpoints.lg}px) and (max-width: ${breakpoints.xl - 1}px)`,

  // Height-based queries
  shortScreen: `@media (max-height: 900px)`,
  veryShortScreen: `@media (max-height: 768px)`,

  // Custom query helper
  custom: (minWidth?: number, maxWidth?: number) => {
    if (minWidth && maxWidth) {
      return `@media (min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`;
    }
    if (minWidth) {
      return `@media (min-width: ${minWidth}px)`;
    }
    if (maxWidth) {
      return `@media (max-width: ${maxWidth}px)`;
    }
    return '';
  },
} as const;

// =============================================================================
// RESPONSIVE SPACING TOKENS
// =============================================================================

export const responsiveSpacing = {
  sidebar: {
    width: { xs: '48px', sm: '52px', md: '56px', lg: '60px', xl: '60px', '2xl': '60px' },
    iconButton: { xs: '36px', sm: '38px', md: '40px', lg: '44px', xl: '44px', '2xl': '44px' },
    iconSize: { xs: '16px', sm: '17px', md: '18px', lg: '20px', xl: '20px', '2xl': '20px' },
    padding: { xs: '6px', sm: '8px', md: '10px', lg: '12px', xl: '12px', '2xl': '12px' },
  },
  header: {
    paddingY: { xs: '12px', sm: '14px', md: '16px', lg: '20px', xl: '24px', '2xl': '24px' },
    paddingX: { xs: '16px', sm: '18px', md: '20px', lg: '24px', xl: '32px', '2xl': '32px' },
    height: { xs: '56px', sm: '60px', md: '64px', lg: '68px', xl: '72px', '2xl': '72px' },
  },
  content: {
    padding: { xs: '12px', sm: '16px', md: '20px', lg: '24px', xl: '32px', '2xl': '32px' },
    gap: { xs: '8px', sm: '12px', md: '16px', lg: '20px', xl: '24px', '2xl': '24px' },
  },
} as const;

// =============================================================================
// RESPONSIVE TYPOGRAPHY TOKENS
// =============================================================================

export const responsiveTypography = {
  title: { xs: '18px', sm: '20px', md: '22px', lg: '24px', xl: '24px', '2xl': '28px' },
  subtitle: { xs: '14px', sm: '15px', md: '16px', lg: '18px', xl: '18px', '2xl': '20px' },
  body: { xs: '13px', sm: '13px', md: '14px', lg: '14px', xl: '15px', '2xl': '15px' },
  small: { xs: '11px', sm: '11px', md: '12px', lg: '12px', xl: '13px', '2xl': '13px' },
} as const;

// =============================================================================
// USE BREAKPOINT HOOK
// =============================================================================

interface BreakpointState {
  width: number;
  height: number;
  isXs: boolean;
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
  isXl: boolean;
  is2xl: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isSmallLaptop: boolean;
  isDesktop: boolean;
  isLargeDesktop: boolean;
  current: BreakpointKey;
}

const getBreakpointState = (width: number, height: number): BreakpointState => {
  const isXs = width < breakpoints.xs;
  const isSm = width >= breakpoints.xs && width < breakpoints.sm;
  const isMd = width >= breakpoints.sm && width < breakpoints.md;
  const isLg = width >= breakpoints.md && width < breakpoints.lg;
  const isXl = width >= breakpoints.lg && width < breakpoints.xl;
  const is2xl = width >= breakpoints.xl;

  let current: BreakpointKey = '2xl';
  if (isXs) current = 'xs';
  else if (isSm) current = 'sm';
  else if (isMd) current = 'md';
  else if (isLg) current = 'lg';
  else if (isXl) current = 'xl';

  return {
    width,
    height,
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    is2xl,
    isMobile: width < breakpoints.sm,
    isTablet: width >= breakpoints.sm && width < breakpoints.md,
    isSmallLaptop: width >= breakpoints.md && width < breakpoints.xl,
    isDesktop: width >= breakpoints.xl,
    isLargeDesktop: width >= breakpoints['2xl'],
    current,
  };
};

export const useBreakpoint = (): BreakpointState => {
  const [state, setState] = useState<BreakpointState>(() => {
    if (typeof window === 'undefined') {
      return getBreakpointState(1920, 1080);
    }
    return getBreakpointState(window.innerWidth, window.innerHeight);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setState(getBreakpointState(window.innerWidth, window.innerHeight));
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return state;
};

// =============================================================================
// CSS HELPER FUNCTIONS
// =============================================================================

export const fluidSize = (min: number, max: number, unit: string = 'px'): string => {
  const slope = (max - min) / (1680 - 320);
  const base = min - slope * 320;
  const vwValue = slope * 100;
  return `clamp(${min}${unit}, ${base.toFixed(2)}${unit} + ${vwValue.toFixed(2)}vw, ${max}${unit})`;
};

export const responsivePaddingCSS = (sizes: Record<BreakpointKey, string>) => css`
  padding: ${sizes['2xl']};

  ${media.belowXl} { padding: ${sizes.xl}; }
  ${media.belowLg} { padding: ${sizes.lg}; }
  ${media.belowMd} { padding: ${sizes.md}; }
  ${media.belowSm} { padding: ${sizes.sm}; }
  ${media.belowXs} { padding: ${sizes.xs}; }
`;

export const responsiveFontCSS = (sizes: Record<BreakpointKey, string>) => css`
  font-size: ${sizes['2xl']};

  ${media.belowXl} { font-size: ${sizes.xl}; }
  ${media.belowLg} { font-size: ${sizes.lg}; }
  ${media.belowMd} { font-size: ${sizes.md}; }
  ${media.belowSm} { font-size: ${sizes.sm}; }
  ${media.belowXs} { font-size: ${sizes.xs}; }
`;

// =============================================================================
// RESPONSIVE MIXINS
// =============================================================================

export const stackOnMobile = css`
  ${media.belowMd} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const hideBelow = (breakpoint: BreakpointKey) => {
  const bp = breakpoints[breakpoint];
  return css`
    @media (max-width: ${bp - 1}px) {
      display: none !important;
    }
  `;
};

export const hideAbove = (breakpoint: BreakpointKey) => {
  const bp = breakpoints[breakpoint];
  return css`
    @media (min-width: ${bp}px) {
      display: none !important;
    }
  `;
};

export const responsiveContentPadding = css`
  padding: ${responsiveSpacing.content.padding['2xl']};

  ${media.belowXl} { padding: ${responsiveSpacing.content.padding.xl}; }
  ${media.belowLg} { padding: ${responsiveSpacing.content.padding.lg}; }
  ${media.belowMd} { padding: ${responsiveSpacing.content.padding.md}; }
  ${media.belowSm} { padding: ${responsiveSpacing.content.padding.sm}; }
`;

export const responsiveTitleFont = css`
  font-size: ${responsiveTypography.title['2xl']};

  ${media.belowXl} { font-size: ${responsiveTypography.title.xl}; }
  ${media.belowLg} { font-size: ${responsiveTypography.title.lg}; }
  ${media.belowMd} { font-size: ${responsiveTypography.title.md}; }
  ${media.belowSm} { font-size: ${responsiveTypography.title.sm}; }
`;

export const responsive = {
  breakpoints,
  media,
  spacing: responsiveSpacing,
  typography: responsiveTypography,
  hooks: { useBreakpoint },
  helpers: {
    fluidSize,
    responsivePaddingCSS,
    responsiveFontCSS,
  },
  mixins: {
    stackOnMobile,
    hideBelow,
    hideAbove,
    responsiveContentPadding,
    responsiveTitleFont,
  },
} as const;

export default responsive;
